import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUserAuth } from "./context/userAuthContext";
import { db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import AppsSlideMenu from "./AppsSlideMenu";
import {
  User,
  Search,
  Bell,
  Menu,
  Plus,
  Settings,
  ArrowLeft,
  FileText,
  Target,
  LogOut,
  ChevronDown,
  Grid3x3,
  MessageCircle,
  Calculator,
  Receipt,
  Shield,
  CreditCard,
  TrendingUp,
  ChevronUp,
  UserCog,
} from "lucide-react";

const MiguelMXNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logOut } = useUserAuth();
  const [userPhotoUrl, setUserPhotoUrl] = useState("");
  const [userName, setUserName] = useState("");
  const [userType, setUserType] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [notifications, setNotifications] = useState(2);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAppsMenu, setShowAppsMenu] = useState(false);

  // Apps for search functionality
  const appsMenuItems = [
    {
      id: "consultas",
      icon: MessageCircle,
      label: "Consultas",
      path: "/consultas",
      description: "Asesoría financiera",
      color: "bg-blue-500",
      keywords: ["consulta", "asesoria", "ayuda", "soporte"],
    },
    {
      id: "polizas",
      icon: Shield,
      label: "Pólizas",
      path: "/polizas",
      description: "Seguros y protección",
      color: "bg-indigo-500",
      keywords: ["seguro", "poliza", "proteccion", "cobertura"],
    },
    {
      id: "tarjetas",
      icon: CreditCard,
      label: "Tarjetas",
      path: "/cards",
      description: "Solicitud de tarjetas de crédito",
      color: "bg-blue-500",
      keywords: ["tarjeta", "credito", "amex", "american express", "solicitud"],
    },
    {
      id: "cashback",
      icon: CreditCard,
      label: "Cashback Pro",
      path: "/cashback",
      description: "Recompensas",
      color: "bg-purple-500",
      badge: "PRO",
      keywords: ["cashback", "recompensa", "puntos", "beneficios"],
    },
    {
      id: "inversiones",
      icon: TrendingUp,
      label: "Inversiones",
      path: "/inversiones",
      description: "Portfolio",
      color: "bg-green-500",
      keywords: ["inversion", "portfolio", "acciones", "rendimiento"],
    },
    {
      id: "ingresos",
      icon: Plus,
      label: "Ingresos",
      path: "/ingresos",
      description: "Entradas de dinero",
      color: "bg-emerald-500",
      keywords: ["ingreso", "entrada", "ganancia", "salario"],
    },
    {
      id: "gastos",
      icon: Receipt,
      label: "Gastos",
      path: "/gastos",
      description: "Control de gastos",
      color: "bg-red-500",
      keywords: ["gasto", "compra", "pago", "expense"],
    },
    {
      id: "calculadora",
      icon: Calculator,
      label: "Calculadora",
      path: "/calculadora",
      description: "Herramientas",
      color: "bg-orange-500",
      keywords: ["calculadora", "calculo", "herramienta", "matematica"],
    },
    {
      id: "metas",
      icon: Target,
      label: "Metas",
      path: "/metas",
      description: "Objetivos financieros",
      color: "bg-pink-500",
      keywords: ["meta", "objetivo", "ahorro", "plan"],
    },
    {
      id: "reportes",
      icon: FileText,
      label: "Reportes",
      path: "/reportes",
      description: "Análisis detallado",
      color: "bg-cyan-500",
      keywords: ["reporte", "analisis", "estadistica", "dashboard"],
    },
  ];

  // Scroll detection for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 10;
      setIsScrolled(scrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = appsMenuItems.filter(
        (app) =>
          app.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.keywords.some((keyword) =>
            keyword.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (user?.uid) {
      // Set user type from context first
      if (user.type) {
        setUserType(user.type);
      }

      const docRef = doc(db, "Users", user.uid);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setUserPhotoUrl(userData.photoUrl || "");
          setUserName(userData.nombre || userData.displayName || "Usuario");
          // Update user type from Firestore
          if (userData.type) {
            setUserType(userData.type);
          }
        }
      });
      return () => unsubscribe();
    } else {
      setUserPhotoUrl("");
      setUserName("");
      setUserType(null);
    }
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim() && searchResults.length > 0) {
      navigate(searchResults[0].path);
      setSearchQuery("");
      setShowSearch(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/login");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const toggleSearch = () => {
    setShowSearch(!showSearch);
    setShowAppsMenu(false);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    setShowAppsMenu(false);
  };

  const toggleAppsMenu = () => {
    setShowAppsMenu(!showAppsMenu);
    setShowUserMenu(false);
    setShowSearch(false);
  };

  const userMenuItems = [
    { icon: User, label: "Mi Perfil", action: () => navigate("/profile") },
    {
      icon: Settings,
      label: "Configuración",
      action: () => navigate("/settings"),
    },
    { icon: FileText, label: "Reportes", action: () => navigate("/reports") },
    {
      icon: Target,
      label: "Metas Financieras",
      action: () => navigate("/goals"),
    },
    // Admin menu items - only show if user is admin
    ...(userType === "admin"
      ? [
          {
            icon: Shield,
            label: "Panel de Administración",
            action: () => navigate("/polizas/admin"),
            admin: true,
          },
          {
            icon: UserCog,
            label: "Gestión de Usuarios",
            action: () => navigate("/polizas/users"),
            admin: true,
          },
        ]
      : []),
    {
      icon: LogOut,
      label: "Cerrar Sesión",
      action: handleLogout,
      danger: true,
    },
  ];

  return (
    <>
      {/* Main Navbar - Modernized */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-xl shadow-lg border-b border-slate-200"
            : "bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Left Section */}
            <div className="flex items-center gap-6">
              {/* Brand - Nuevo Logo */}
              <Link
                to="/dashboard"
                className="flex items-center gap-3 transform hover:scale-105 transition-all duration-200"
              >
                <div className="h-10 lg:h-12 flex items-center">
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/xfinance-12e10.firebasestorage.app/o/Logos%2Fmiguelnomasdamn.png?alt=media&token=92c49b72-0124-4622-a8e5-5f181e8a462d"
                    alt="MiguelMX"
                    className="h-8 lg:h-10 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="w-32 h-10 bg-gradient-to-r from-[#14840c] to-green-700 rounded-lg items-center justify-center shadow-lg hidden">
                    <span className="text-white font-bold text-lg">
                      MiguelMX
                    </span>
                  </div>
                </div>
              </Link>

              {/* Apps Button - Desktop */}
              <button
                onClick={toggleAppsMenu}
                className={`hidden lg:flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all duration-200 relative ${
                  showAppsMenu
                    ? "bg-[#14840c] text-white shadow-lg"
                    : "text-slate-600 hover:text-[#14840c] hover:bg-green-50"
                }`}
                title="Apps de MiguelMX"
              >
                <Grid3x3
                  className={`w-5 h-5 ${showAppsMenu ? "text-white" : ""}`}
                />
                <span className="text-sm font-medium">Apps</span>
                {showAppsMenu ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Center Section - Futuristic Search */}
            <div className="hidden md:block flex-1 max-w-2xl mx-8">
              <div className="relative group">
                {/* Futuristic Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#033937] via-[#006363] to-[#033937] rounded-2xl opacity-20 group-focus-within:opacity-40 transition-opacity duration-300 blur-sm"></div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-[#006363] group-focus-within:text-[#5ec400] transition-all duration-300 drop-shadow-sm" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch(e)}
                    placeholder="Buscar apps, herramientas financieras..."
                    className="block w-full pl-12 pr-12 py-3 border-2 border-[#033937]/30 rounded-2xl bg-white/95 backdrop-blur-sm placeholder-[#006363]/70 focus:outline-none focus:ring-2 focus:ring-[#5ec400] focus:border-[#5ec400] focus:bg-white transition-all duration-300 text-sm font-medium text-[#033937] shadow-lg group-hover:shadow-xl group-hover:border-[#006363] group-focus-within:shadow-[0_0_20px_rgba(94,196,0,0.3)]"
                  />
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                    <kbd className="px-3 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-[#033937] to-[#006363] border border-[#5ec400]/50 rounded-lg shadow-lg backdrop-blur-sm group-focus-within:border-[#5ec400] group-focus-within:shadow-[0_0_10px_rgba(94,196,0,0.5)] transition-all duration-300">
                      ⌘K
                    </kbd>
                  </div>
                </div>

                {/* Search Results Dropdown - Futuristic */}
                {searchQuery && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-3 bg-gradient-to-b from-[#033937] to-[#006363] rounded-2xl shadow-2xl border border-[#5ec400]/30 py-3 z-50 max-h-80 overflow-y-auto backdrop-blur-lg">
                    <div className="px-4 py-2 text-xs font-bold text-white uppercase tracking-wider border-b border-[#5ec400]/20 mb-2">
                      <span className="drop-shadow-lg">
                        ✨ Apps encontradas ({searchResults.length})
                      </span>
                    </div>
                    {searchResults.map((app) => {
                      const Icon = app.icon;
                      return (
                        <button
                          key={app.id}
                          onClick={() => {
                            navigate(app.path);
                            setSearchQuery("");
                          }}
                          className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[#5ec400]/20 transition-all duration-300 text-left group/item border-l-4 border-transparent hover:border-[#5ec400] hover:shadow-[0_0_15px_rgba(94,196,0,0.3)]"
                        >
                          <div
                            className={`w-12 h-12 ${app.color} rounded-xl flex items-center justify-center shadow-lg group-hover/item:shadow-[0_0_20px_rgba(94,196,0,0.4)] transition-all duration-300`}
                          >
                            <Icon className="w-6 h-6 text-white drop-shadow-lg" />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-white drop-shadow-sm group-hover/item:text-[#5ec400] transition-colors duration-300">
                              {app.label}
                            </p>
                            <p className="text-sm text-white/70 group-hover/item:text-white transition-colors duration-300">
                              {app.description}
                            </p>
                          </div>
                          {app.badge && (
                            <span className="bg-[#5ec400] text-[#033937] text-xs px-3 py-1 rounded-full font-bold shadow-lg border border-[#5ec400]/50">
                              {app.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* Mobile Search Button */}
              <button
                onClick={toggleSearch}
                className="md:hidden p-2.5 rounded-xl hover:bg-green-50 hover:text-[#14840c] transition-colors"
              >
                <Search className="w-5 h-5 text-slate-600" />
              </button>

              {/* Mobile Apps Button */}
              <button
                onClick={toggleAppsMenu}
                className="lg:hidden p-2.5 rounded-xl hover:bg-green-50 hover:text-[#14840c] transition-colors"
              >
                <Grid3x3 className="w-5 h-5 text-slate-600" />
              </button>

              {/* Quick Actions - Desktop */}
              <div className="hidden lg:flex items-center gap-2">
                {/* Admin Dashboard Button - Only for admins */}
                {userType === "admin" && (
                  <button
                    onClick={() => navigate("/polizas/admin")}
                    className="relative p-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700 transition-all duration-200 group shadow-md hover:shadow-lg"
                    title="Panel de Administración"
                  >
                    <Shield className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => navigate("/quick-add")}
                  className="relative p-2.5 rounded-xl hover:bg-green-50 transition-all duration-200 group"
                  title="Acción rápida"
                >
                  <Plus className="w-5 h-5 text-slate-600 group-hover:text-[#14840c]" />
                </button>
                <button
                  onClick={() => navigate("/notifications")}
                  className="relative p-2.5 rounded-xl hover:bg-green-50 transition-all duration-200 group"
                  title="Notificaciones"
                >
                  <Bell className="w-5 h-5 text-slate-600 group-hover:text-[#14840c]" />
                  {notifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#14840c] text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {notifications > 9 ? "9+" : notifications}
                    </span>
                  )}
                </button>
              </div>

              {/* Admin Dashboard Button - Mobile - Only for admins */}
              {userType === "admin" && (
                <button
                  onClick={() => navigate("/polizas/admin")}
                  className="lg:hidden relative p-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md"
                  title="Panel de Administración"
                >
                  <Shield className="w-5 h-5" />
                </button>
              )}

              {/* Notifications - Mobile */}
              <button
                onClick={() => navigate("/notifications")}
                className="lg:hidden relative p-2.5 rounded-xl hover:bg-green-50 hover:text-[#14840c] transition-colors"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#14840c] text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {notifications > 9 ? "9+" : notifications}
                  </span>
                )}
              </button>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center gap-3 p-1.5 rounded-xl hover:bg-green-50 transition-all duration-200 group"
                >
                  <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl overflow-hidden border-2 border-slate-200 group-hover:border-[#14840c] transition-all duration-200 shadow-sm">
                    {userPhotoUrl ? (
                      <img
                        src={userPhotoUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center group-hover:from-green-100 group-hover:to-green-200 transition-all duration-200">
                        <User className="w-5 h-5 lg:w-6 lg:h-6 text-slate-400 group-hover:text-[#14840c]" />
                      </div>
                    )}
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-semibold text-slate-900 truncate max-w-24">
                      {userName}
                    </p>
                    <p className="text-xs text-slate-500 font-medium">
                      MiguelMX Pro
                    </p>
                  </div>
                  <ChevronDown className="hidden lg:block w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-3 w-72 bg-white rounded-2xl shadow-xl border border-slate-200 py-3 z-50">
                    <div className="px-6 py-4 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl overflow-hidden border-2 border-slate-200">
                          {userPhotoUrl ? (
                            <img
                              src={userPhotoUrl}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                              <User className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">{userName}</p>
                          <p className="text-sm text-slate-500">
                            {user?.email}
                          </p>
                          <span className="inline-block bg-[#14840c] text-white text-xs px-2 py-1 rounded-full font-bold mt-1">
                            PRO
                          </span>
                        </div>
                      </div>
                    </div>
                    {userMenuItems.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            item.action();
                            setShowUserMenu(false);
                          }}
                          className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${
                            item.danger
                              ? "text-red-600 hover:bg-red-50"
                              : item.admin
                                ? "text-purple-600 hover:bg-purple-50 border-l-4 border-purple-500"
                                : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                          {item.admin && (
                            <span className="ml-auto bg-purple-100 text-purple-600 text-xs px-2 py-0.5 rounded-full font-bold">
                              ADMIN
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Search Bar - Futuristic */}
          <div
            className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
              showSearch ? "max-h-32 opacity-100 pb-4" : "max-h-0 opacity-0"
            }`}
          >
            <div className="relative pt-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleSearch}
                  className="p-2 rounded-xl hover:bg-green-50 hover:text-[#14840c] transition-colors flex-shrink-0"
                >
                  <ArrowLeft className="w-5 h-5 text-slate-600" />
                </button>
                <div className="flex-1 relative">
                  {/* Mobile Futuristic Search */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#033937] via-[#006363] to-[#033937] rounded-2xl opacity-20 blur-sm"></div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch(e)}
                    placeholder="Buscar apps de MiguelMX..."
                    className="relative w-full px-4 py-3 pl-12 pr-4 rounded-2xl bg-white/95 backdrop-blur-sm border-2 border-[#033937]/30 focus:ring-2 focus:ring-[#5ec400] focus:border-[#5ec400] focus:bg-white transition-all duration-300 font-medium text-sm text-[#033937] placeholder-[#006363]/70 shadow-lg focus:shadow-[0_0_20px_rgba(94,196,0,0.3)]"
                    autoFocus
                  />
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#006363] w-5 h-5 drop-shadow-sm" />
                </div>
              </div>

              {/* Mobile Search Results */}
              {searchQuery && searchResults.length > 0 && (
                <div className="mt-3 bg-gradient-to-b from-[#033937] to-[#006363] rounded-2xl shadow-2xl border border-[#5ec400]/30 py-3 max-h-60 overflow-y-auto backdrop-blur-lg">
                  <div className="px-4 py-2 text-xs font-bold text-white uppercase tracking-wider border-b border-[#5ec400]/20 mb-2">
                    <span className="drop-shadow-lg">
                      ✨ Apps encontradas ({searchResults.length})
                    </span>
                  </div>
                  {searchResults.map((app) => {
                    const Icon = app.icon;
                    return (
                      <button
                        key={app.id}
                        onClick={() => {
                          navigate(app.path);
                          setSearchQuery("");
                          setShowSearch(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#5ec400]/20 transition-all duration-300 text-left group/item border-l-4 border-transparent hover:border-[#5ec400]"
                      >
                        <div
                          className={`w-10 h-10 ${app.color} rounded-xl flex items-center justify-center shadow-lg group-hover/item:shadow-[0_0_15px_rgba(94,196,0,0.4)] transition-all duration-300`}
                        >
                          <Icon className="w-5 h-5 text-white drop-shadow-lg" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-white text-sm drop-shadow-sm group-hover/item:text-[#5ec400] transition-colors duration-300">
                            {app.label}
                          </p>
                          <p className="text-xs text-white/70 group-hover/item:text-white transition-colors duration-300">
                            {app.description}
                          </p>
                        </div>
                        {app.badge && (
                          <span className="bg-[#5ec400] text-[#033937] text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                            {app.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Apps Slide Menu Component */}
      <AppsSlideMenu
        showAppsMenu={showAppsMenu}
        setShowAppsMenu={setShowAppsMenu}
      />

      {/* Overlay to close menus */}
      {(showUserMenu || showAppsMenu) && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => {
            setShowUserMenu(false);
            setShowAppsMenu(false);
          }}
        />
      )}
    </>
  );
};

export default MiguelMXNavbar;
